import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Container, Grid, Typography, Link } from "@material-ui/core";
import gsap from "gsap";
import SplitText from "gsap/SplitText";
import Header from "./AnimatedHeader";
import SubHeader from "./SubHeader";

gsap.registerPlugin(SplitText);

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    visibility: "hidden",
  },
  mainContent: {
    minHeight: theme.sizes.bodyMinHeight,
  },
  gridContainer: {
    minHeight: theme.sizes.bodyMinHeight,
    position: "relative",
    paddingTop: theme.spacing(5),
  },
  gridItem: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  signingOff: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderTop: "1px solid " + theme.palette.dividerColor,
  },
  sectionTitle: {},
}));

function About(props) {
  const containerRef = React.useRef();

  useEffect(() => {
    if (sessionStorage.getItem("linkedFromHomePage")) {
      window.scrollTo(0, 0);
      sessionStorage.removeItem("linkedFromHomePage");
    }
    const container = containerRef.current;
    gsap.to(container, { autoAlpha: 1 });
  });

  const classes = useStyles();

  return (
    <div ref={containerRef} className={classes.root}>
      <Header text="about" />
      <SubHeader text="a bit about this site and its author" />
      <div className={classes.mainContent}>
        <Container maxWidth="md">
          <Grid
            className={classes.gridContainer}
            container
            justify="center"
            alignItems="center"
          >
            {/* the site */}
            <Grid className={classes.gridItem} item xs={12}>
              <Typography align="center">
                This is a demo site for{" "}
                <Link
                  href="https://greensock.com/gsap/"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  GSAP
                </Link>{" "}
                animations, built with{" "}
                <Link
                  href="https://reactjs.org"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  React
                </Link>{" "}
                and{" "}
                <Link
                  href="https://material-ui.com"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Material UI
                </Link>
                .
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <Typography variant="h5" align="center" gutterBottom>
                GSAP
              </Typography>
              <Typography gutterBottom>
                <Link
                  href="https://greensock.com/gsap/"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  GreenSock Animation Platform
                </Link>{" "}
                is a powerful, lightweight, and widely supported JavaScript
                animation library for the web. Industry leaders like Google,
                Apple, Microsoft, and Amazon use GSAP for animations on their
                websites.
              </Typography>
              <Typography>
                GSAP makes complex animations possible for the web with{" "}
                <Link
                  href="https://greensock.com/docs/v3/GSAP/Timeline"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Timeline
                </Link>{" "}
                sequencing and{" "}
                <Link
                  href="https://greensock.com/docs/v3/Plugins/GSDevTools"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  DevTools
                </Link>{" "}
                for fine-tuned effects at 60 frames per second. GreenSock offers
                premium plugins for professional animators like{" "}
                <Link
                  href="https://greensock.com/morphsvg/"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  MorphSVG
                </Link>{" "}
                and{" "}
                <Link
                  href="https://greensock.com/docs/v3/Plugins/SplitText"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  SplitText
                </Link>
                .
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <Typography variant="h5" align="center" gutterBottom>
                React
              </Typography>
              <Typography gutterBottom>
                <Link
                  href="https://reactjs.org"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  React
                </Link>{" "}
                is a JavaScript library for creating user interfaces. It uses a
                component-based system that keeps code for each part of the UI
                separate and simple to write, maintain, and use in any project.
              </Typography>
              <Typography>
                React is an open source project maintained by Facebook, has an
                active developer community, and powers over 7.4 million live
                websites (
                <Link
                  href="https://trends.builtwith.com/javascript/React"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  as of
                </Link>{" "}
                June 2021).
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <Typography variant="h5" align="center" gutterBottom>
                Material UI
              </Typography>
              <Typography gutterBottom>
                <Link
                  href="https://material-ui.com"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Material UI
                </Link>{" "}
                is a React framework based on Google's{" "}
                <Link
                  href="https://material.io/design"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Material Design
                </Link>{" "}
                guidelines.
              </Typography>
              <Typography>
                Material UI provides React components for fast and easy web
                development, and is{" "}
                <Link
                  href="https://material-ui.com/company/about/"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  used by over 1 million developers in 180 countries
                </Link>
                .
              </Typography>
            </Grid>

            {/* the author */}
            <Grid item xs={12} className={classes.gridItem}>
              <Typography variant="h5" align="center" gutterBottom>
                Jonathan Harrison
              </Typography>
              <Typography gutterBottom>
                I studied literature and philosophy in college, but have always
                loved technology. I got hooked on programming after taking
                Harvard's{" "}
                <Link
                  href="https://cs50.harvard.edu/college/2021/fall/"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  CS50
                </Link>
                , and went pro on the web when my partner Kim and I took our
                small business online.
              </Typography>
              <Typography gutterBottom>
                In the years since, I've been the developer and webmaster for
                our e-commerce business, and have built sites, games, themes,
                animations, and user interfaces for clients as a freelancer.
              </Typography>
              <Typography gutterBottom>
                Recently, I've been focusing on web animation with GSAP, UI
                development with React, and game development with{" "}
                <Link
                  href="https://www.unrealengine.com/en-US/"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Unreal Engine
                </Link>{" "}
                and{" "}
                <Link
                  href="https://unity.com"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Unity
                </Link>
                .
              </Typography>
              <Typography gutterBottom>
                I am currently seeking clients for front end projects with GSAP
                and React.{" "}
                <Link
                  href="https://www.upwork.com/freelancers/jonathanharrison10"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Contact me
                </Link>{" "}
                through Upwork with any questions.
              </Typography>
            </Grid>

            <Grid className={classes.signingOff} item xs={12}>
              <Typography align="center" gutterBottom>
                Looking for the old site?{" "}
                <Link
                  href="https://jonathanharrison.us/archive/foundation-gsap-demo-site/index.html"
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                >
                  Here you go
                </Link>
                .
              </Typography>
              <Typography align="center">Thanks for visiting.</Typography>
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
}

export default About;
